import * as Yup from "yup";
import { setIn } from "formik";
import { normalizeCompanyWebsiteUrl } from "./clientProfileFormHelpers";

function hasStoredFileRef(value) {
  return typeof value === "string" && value.trim().length > 0;
}

function insurancePolicyFileField(existingKey) {
  return Yup.mixed()
    .nullable()
    .notRequired()
    .test("insurancePolicy", "Required", function (value) {
      const existing = this.parent?.[existingKey];
      if (hasStoredFileRef(existing)) return true;
      if (value == null || value === "") {
        return this.createError({ message: "Upload policy document" });
      }
      if (!(value instanceof File)) {
        return this.createError({ message: "Must be a PDF or image" });
      }
      return /\.(pdf|jpe?g|png|webp)$/i.test(value.name);
    });
}

function fileOrExisting(existingKey) {
  return Yup.mixed()
    .nullable()
    .notRequired()
    .test("fileOrExisting", "Required", function (value) {
      const existing = this.parent?.[existingKey];
      if (hasStoredFileRef(existing)) return true;
      if (value == null || value === "") {
        return this.createError({ message: "Required" });
      }
      if (!(value instanceof File)) {
        return this.createError({ message: "Must be a PDF or image" });
      }
      return /\.(pdf|jpe?g|png|webp)$/i.test(value.name);
    });
}

const storedFileMeta = {
  existingIdUrl: Yup.string().nullable().notRequired(),
  existingProofUrl: Yup.string().nullable().notRequired(),
};

const directorEntrySchema = Yup.object({
  fullName: Yup.string().trim().required("Required"),
  ...storedFileMeta,
  idFile: fileOrExisting("existingIdUrl"),
  proofOfAddressFile: fileOrExisting("existingProofUrl"),
});

const ownerEntrySchema = Yup.object({
  fullName: Yup.string().trim().required("Required"),
  percentageOfOwnership: Yup.number()
    .typeError("Must be a number")
    .min(25, "Must be at least 25%")
    .max(100, "Max 100%")
    .required("Required"),
  ...storedFileMeta,
  idFile: fileOrExisting("existingIdUrl"),
  proofOfAddressFile: fileOrExisting("existingProofUrl"),
});

const primaryInformationSchema = Yup.object({
  fullName: Yup.string().trim().required("Please enter full name"),
  email: Yup.string().trim().email("Invalid email").required("Required"),
  phoneNumber: Yup.string()
    .trim()
    .matches(/^[0-9]+$/, "Digits only")
    .min(10, "Min 10 digits")
    .max(15, "Max 15 digits")
    .required("Required"),
  position: Yup.string().trim().required("Required"),
});

const companyInformationSchema = Yup.object({
  registeredCompanyName: Yup.string().trim().required("Required"),
  tradingName: Yup.string().trim(),
  companyRegistrationNumber: Yup.string().trim().required("Required"),
  vatRegistrationNumber: Yup.string().trim().required("Required"),
  registeredOfficeAddress: Yup.string().trim().required("Required"),
  tradingAddress: Yup.string().trim().required("Required"),
  organisationType: Yup.string()
    .oneOf([
      "Ltd",
      "PLC",
      "Charity",
      "Partnership",
      "Sole Trader",
      "Public Sector",
      "Other",
    ])
    .required("Required"),
  industrySector: Yup.string().trim().required("Required"),
  companyWebsite: Yup.string()
    .transform((value) => normalizeCompanyWebsiteUrl(value))
    .required("Required")
    .url("Enter a valid URL"),
  isWorkplaceDisabilityFriendly: Yup.string()
    .oneOf(["yes", "no", "unsure"])
    .required("Required"),
});

const financeBillingSchema = Yup.object({
  fullName: Yup.string().trim().required("Required"),
  email: Yup.string().trim().email("Invalid email").required("Required"),
  phone: Yup.string()
    .trim()
    .matches(/^[0-9]+$/, "Digits only")
    .min(10, "Min 10 digits")
    .max(15, "Max 15 digits")
    .required("Required"),
});

const healthAndSafetyContactSchema = Yup.object({
  fullName: Yup.string().trim().required("Required"),
  email: Yup.string().trim().email("Invalid email").required("Required"),
  phoneNumber: Yup.string()
    .trim()
    .matches(/^[0-9]+$/, "Digits only")
    .min(10, "Min 10 digits")
    .max(15, "Max 15 digits")
    .required("Required"),
});

const ownershipControlDeclarationSchema = Yup.object({
  ownershipCompanyType: Yup.string()
    .oneOf(["individuals", "corporate"])
    .required("Select ownership type"),
  adminStatus: Yup.string().nullable().notRequired(),
  adminReason: Yup.string().nullable().notRequired(),
  directors: Yup.array().of(directorEntrySchema).min(1, "Add at least one director"),
  individualOwners: Yup.array().when("ownershipCompanyType", {
    is: "individuals",
    then: (schema) =>
      schema.of(ownerEntrySchema).min(1, "Add at least one shareholder"),
    otherwise: () => Yup.array(),
  }),
  corporateUltimateOwners: Yup.array().when("ownershipCompanyType", {
    is: "corporate",
    then: (schema) =>
      schema.of(ownerEntrySchema).min(1, "Add at least one ultimate shareholder"),
    otherwise: () => Yup.array(),
  }),
});

const insuranceSchema = Yup.object({
  existingEmployersLiability: Yup.string().nullable().notRequired(),
  existingPublicLiability: Yup.string().nullable().notRequired(),
  employersLiability: insurancePolicyFileField("existingEmployersLiability"),
  publicLiability: insurancePolicyFileField("existingPublicLiability"),
});

export const CLIENT_PROFILE_SECTIONS = [
  "primaryInformation",
  "companyInformation",
  "financeBilling",
  "healthAndSafetyContact",
  "ownershipControlDeclaration",
  "insurance",
];

const sectionSchemaMap = {
  primaryInformation: primaryInformationSchema,
  companyInformation: companyInformationSchema,
  financeBilling: financeBillingSchema,
  healthAndSafetyContact: healthAndSafetyContactSchema,
  ownershipControlDeclaration: ownershipControlDeclarationSchema,
  insurance: insuranceSchema,
};

export function getClientProfileSectionSchema(section) {
  return sectionSchemaMap[section] || null;
}

export function buildClientProfileValidationSchema() {
  return Yup.object({
    role: Yup.string().oneOf(["client"]).required(),
    primaryInformation: primaryInformationSchema,
    companyInformation: companyInformationSchema,
    financeBilling: financeBillingSchema,
    healthAndSafetyContact: healthAndSafetyContactSchema,
    ownershipControlDeclaration: ownershipControlDeclarationSchema,
    insurance: insuranceSchema,
  });
}

export function normalizeValidationPath(path) {
  return String(path || "").replace(/\[(\d+)\]/g, ".$1");
}

export function applySectionValidationErrors(
  yupError,
  section,
  setErrors,
  setFieldTouched,
  currentErrors
) {
  const entries =
    yupError?.inner?.length > 0
      ? yupError.inner
      : yupError?.path
        ? [yupError]
        : [];

  let sectionErrors = {};

  entries.forEach((e) => {
    if (!e.path) return;
    const normalized = normalizeValidationPath(e.path);
    sectionErrors = setIn(sectionErrors, normalized, e.message);
    setFieldTouched(`${section}.${e.path}`, true, false);
  });

  if (Object.keys(sectionErrors).length > 0) {
    setErrors({ ...currentErrors, [section]: sectionErrors });
  }

  return sectionErrors;
}

export function clearSectionValidationErrors(section, errors, setErrors) {
  if (!errors?.[section]) return;
  const nextErrors = { ...errors };
  delete nextErrors[section];
  setErrors(nextErrors);
}

function touchDeep(prefix, value, setFieldTouched) {
  if (value == null) {
    setFieldTouched(prefix, true, false);
    return;
  }
  if (Array.isArray(value)) {
    value.forEach((item, index) => {
      touchDeep(`${prefix}[${index}]`, item, setFieldTouched);
    });
    return;
  }
  if (typeof value === "object" && !(value instanceof File)) {
    Object.keys(value).forEach((key) => {
      touchDeep(`${prefix}.${key}`, value[key], setFieldTouched);
    });
    return;
  }
  setFieldTouched(prefix, true, false);
}

export function touchClientProfileSection(section, values, setFieldTouched) {
  if (!values?.[section]) return;
  touchDeep(section, values[section], setFieldTouched);
}
